import { usePostHogState } from "@/app/_providers/posthog";
import posthog from "posthog-js";
import { useFeatureFlagPayload } from "posthog-js/react";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { EventName } from "@mw/shared/analyticsEventNames";
import { captureEvent } from "./analytics";
import { FEATURE_FLAGS } from "./feature-flags";

const DEFAULT_SAMPLE_RATE = 0.001;

interface SessionRecordingPayload {
  routes: string[];
}

function isValidPayload(payload: unknown): payload is SessionRecordingPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  if (!Array.isArray(p.routes)) return false;
  return p.routes.every((r: unknown) => typeof r === "string");
}

export const useSessionRecording = () => {
  const pathname = usePathname();
  const isPostHogLoaded = usePostHogState(state => state.isLoaded);
  const payload = useFeatureFlagPayload(FEATURE_FLAGS.ENABLE_SESSION_RECORDING);
  const samplingDecisionMade = useRef(false);

  useEffect(() => {
    if (!isPostHogLoaded || !pathname) return;
    if (samplingDecisionMade.current || posthog.sessionRecordingStarted()) return;
    if (!isValidPayload(payload)) return;

    const matchingPattern = payload.routes.find(r => pathname.startsWith(r));
    if (!matchingPattern) return;

    samplingDecisionMade.current = true;

    if (Math.random() >= DEFAULT_SAMPLE_RATE) return;

    captureEvent(EventName.mw_session_recording_started, {
      route_pattern: matchingPattern,
      sample_rate: DEFAULT_SAMPLE_RATE,
    });

    posthog.startSessionRecording(true);
  }, [pathname, isPostHogLoaded, payload]);
};
